import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, Text, ButtonAnt } from '@pfmam/pfmam';
import { Select, Checkbox, Input, AutoComplete, DatePicker } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Box } from '../../styles/globalStyle';
import moment from 'moment';
import { OverrideStyle } from '../../common/components/datePicker';

const { Option } = Select;

/* ─────────────────────────────────────────
   Layout
───────────────────────────────────────── */

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${theme.font_size.size_l};
  font-weight: 500;
  color: ${theme.colors.black_secondary};
  font-family: 'Inter', sans-serif;
  line-height: 20px;
`;

const RequiredAsterisk = styled.span`
  color: ${theme.colors.danger_red};
  font-size: 14px;
  line-height: 1;
`;

const ErrorText = styled.span`
  color: ${theme.colors.danger_red};
  font-size: ${theme.font_size.size_l};
  line-height: 18px;
`;

/* ─────────────────────────────────────────
   Section divider / collapsible
───────────────────────────────────────── */

const SectionBlock = styled.div`
  border-top: 1px solid ${theme.colors.grey_fossil};
  padding-top: 8px;
  margin-top: 4px;
`;

const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 10px 12px;
  cursor: pointer;
  font-size: ${theme.font_size.size_xl};
  font-weight: 600;
  color: ${theme.colors.black_secondary};
  font-family: 'Inter', sans-serif;
  line-height: 22px;
  width: 100%;
  border-radius: 6px;
  transition: background 0.15s ease;
  &:hover {
    background: ${theme.colors.light_grey};
  }
`;

const CollapseIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${theme.colors.light_grey};
  font-size: 10px;
  color: ${theme.colors.black_secondary};
  flex-shrink: 0;
  transition: background 0.15s ease;

  ${CollapseToggle}:hover & {
    background: ${theme.colors.grey};
  }
`;

const OptionalContent = styled.div`
  padding: 12px 0 4px 0;
`;

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* ─────────────────────────────────────────
   Footer
───────────────────────────────────────── */

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid ${theme.colors.grey_fossil};
  margin-top: 8px;
`;

const ConfirmButton = styled(ButtonAnt)`
  &&& {
    background: ${(props) => props.linktextcolor};
    color: white;
    transition: box-shadow 0.2s ease, transform 0.1s ease;
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
      transform: translateY(-1px);
      filter: brightness(0.93);
    }
    &:active {
      transform: translateY(0);
      box-shadow: none;
      filter: brightness(0.85);
    }
  }
`;

/* ─────────────────────────────────────────
   Review screen
───────────────────────────────────────── */

const ReviewCard = styled.div`
  border: 1px solid ${theme.colors.grey_fossil};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const ReviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 12px 16px;
  gap: 16px;
  background: ${(props) => (props.shaded ? theme.colors.light_grey : theme.colors.white)};
  border-bottom: 1px solid ${theme.colors.grey_fossil};
  &:last-child {
    border-bottom: 0;
  }
`;

const ReviewLabel = styled.span`
  font-size: ${theme.font_size.size_l};
  color: ${theme.colors.grey_tertiary};
  font-family: 'Inter', sans-serif;
  line-height: 20px;
  white-space: nowrap;
`;

const ReviewValue = styled.span`
  font-size: ${theme.font_size.size_xl};
  color: ${theme.colors.clean_black};
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  line-height: 20px;
  text-align: right;
`;

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1).padStart(2, '0'),
}));

const currentYear = new Date().getFullYear();
// Current year + past 5 years (6 total), ascending
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

const MAX_MONTH_END_BALANCE = 9999999999999999.99;

const OPTIONAL_FIELDS = [
  'Issuer Ticker',
  'Accrued Interest',
  'YTM at Cost',
  'YTM Market',
  'SEC Type',
  'Maturity Date',
  'S&P Rating',
  'Moody Rating',
  'Fitch Rating',
  'WAM',
  'Effective Duration',
];

const CURRENCY_FIELDS = new Set([
  'Accrued Interest',
]);

const NUMBER_FIELDS = new Set([
  'Effective Duration',
]);

const MAX_ACCRUED_INTEREST_VALUE = 99999999.99;
const MAX_EFFECTIVE_DURATION_VALUE = 999.9999;
const MAX_WAM_VALUE = 99.99;

const PERCENT_FIELDS = new Set([
  'YTM at Cost',
  'YTM Market',
]);

const RATING_FIELD_TO_KEY = {
  'S&P Rating': 'sp',
  'Moody Rating': 'moody',
  'Fitch Rating': 'fitch',
};

const formatBalanceDisplay = (value) => {
  // Match SQL decimal(18,2): max 16 integer digits and 2 decimal digits.
  const stripped = String(value || '').replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  const integerPart = (parts[0] || '').slice(0, 16);
  const decimalPart = parts.length > 1 ? parts.slice(1).join('').slice(0, 2) : undefined;
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (decimalPart !== undefined) {
    return `${formattedIntegerPart}.${decimalPart}`;
  }

  return formattedIntegerPart;
};

const parseBalance = (displayValue) => {
  const num = parseFloat((displayValue || '').replace(/,/g, ''));
  return isNaN(num) ? null : num;
};

const sanitizePercentValue = (value) => {
  let val = (value || '').replace(/[^0-9.]/g, '');
  const parts = val.split('.');
  if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
  const normalized = val.split('.');
  if (normalized[1] !== undefined) {
    val = normalized[0] + '.' + normalized[1].slice(0, 2);
  }

  const numeric = parseFloat(val);
  if (!isNaN(numeric) && numeric > 10) {
    return '10.00';
  }
  return val;
};

const sanitizeNumericWithScale = (value, maxIntegerDigits = 12, maxDecimals = 2) => {
  let val = String(value || '').replace(/[^0-9.]/g, '');
  const parts = val.split('.');
  if (parts.length > 2) {
    val = parts[0] + '.' + parts.slice(1).join('');
  }

  const normalized = val.split('.');
  const intPart = (normalized[0] || '').slice(0, maxIntegerDigits);
  const decimalPart = normalized[1] !== undefined ? normalized[1].slice(0, maxDecimals) : undefined;

  return decimalPart !== undefined ? `${intPart}.${decimalPart}` : intPart;
};

const parseNumericOptional = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = parseFloat(String(value).replace(/,/g, ''));
  return isNaN(num) ? null : num;
};

const sanitizeHoldingDescription = (value) =>
  String(value || '').replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, '').slice(0, 255);

const containsEmoji = (value) => /[\p{Extended_Pictographic}\uFE0F\u200D]/u.test(String(value || ''));

const formatNumericWithCommas = (value, maxDecimals = 2) => {
  const raw = String(value || '').replace(/,/g, '');
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = (parts[0] || '').replace(/^0+(?=\d)/, '') || '0';
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts[1] !== undefined) {
    return `${formattedInt}.${parts[1].slice(0, maxDecimals)}`;
  }
  return formattedInt;
};

const isPastDate = (value) => {
  const parsed = moment(value, 'MM/DD/YYYY', true);
  if (!parsed.isValid()) return false;
  return parsed.isBefore(moment().startOf('day'), 'day');
};

const ThirdPartyHoldingsAddModal = ({ onSubmit, onCancel, primaryColor, submitting, submitError, linkedAccounts = [], onBeforeReview = () => Promise.resolve(false), securityDescriptionOptions = [], securityTypeOptions = [], portfolioOptions = [], ratingOptions = { sp: [], moody: [], fitch: [] } }) => {
  // Track whether the current value was selected from the API list or typed free-form
  const [isApiSelection, setIsApiSelection] = useState(false);
  const [securityDescription, setSecurityDescription] = useState('');
  const [balanceDisplay, setBalanceDisplay] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [includeMonthly, setIncludeMonthly] = useState(false);
  const [optionalFields, setOptionalFields] = useState({});
  const [errors, setErrors] = useState({});
  const [showReview, setShowReview] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [showOptional, setShowOptional] = useState(false);
  const [linkedPfmamAccount, setLinkedPfmamAccount] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  const handleBalanceChange = (e) => {
    setBalanceDisplay(formatBalanceDisplay(e.target.value));
  };

  const handleBalanceBlur = (e) => {
    const num = parseBalance(e.target.value);
    if (num !== null && e.target.value.trim()) {
      setBalanceDisplay(
        num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      );
    }
    if (errors.balance) setErrors((prev) => ({ ...prev, balance: undefined }));
  };

  const handleOptionalFieldChange = (field, value) => {
    setOptionalFields((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!securityDescription.trim()) {
      newErrors.securityDescription = 'Holding Description is required';
    } else if (containsEmoji(securityDescription)) {
      newErrors.securityDescription = 'Holding Description cannot contain emoji characters';
    }
    if (!balanceDisplay.trim()) {
      newErrors.balance = 'Month End Closing Market Value is required';
    } else if (parseBalance(balanceDisplay) === null) {
      newErrors.balance = 'Please enter a valid number';
    } else if (parseBalance(balanceDisplay) > MAX_MONTH_END_BALANCE) {
      newErrors.balance = 'Month End Closing Market Value cannot exceed 9,999,999,999,999,999.99';
    }
    if (!selectedMonth) {
      newErrors.monthEnd = 'Please select a month and year';
    }
    if (!selectedYear) {
      newErrors.monthEnd = 'Please select a month and year';
    }
    if (includeMonthly && !linkedPfmamAccount) {
      newErrors.linkedPfmamAccount = 'Please select a linked PFMAM account';
    }
    if (optionalFields['Maturity Date']) {
      if (isPastDate(optionalFields['Maturity Date'])) {
        newErrors.maturityDate = 'Maturity Date cannot be earlier than today';
      }
    }
    if (optionalFields['YTM at Cost']) {
      const ytmAtCost = parseFloat(optionalFields['YTM at Cost']);
      if (!isNaN(ytmAtCost) && ytmAtCost > 10) {
        newErrors.ytmAtCost = 'YTM at Cost cannot be greater than 10.00%';
      }
    }
    if (optionalFields['YTM Market']) {
      const ytmMarket = parseFloat(optionalFields['YTM Market']);
      if (!isNaN(ytmMarket) && ytmMarket > 10) {
        newErrors.ytmMarket = 'YTM Market cannot be greater than 10.00%';
      }
    }
    if (optionalFields['Issuer Ticker']) {
      const issuerTicker = String(optionalFields['Issuer Ticker']);
      if (!/^[a-z0-9 ]+$/i.test(issuerTicker)) {
        newErrors.issuerTicker = 'Issuer Ticker must contain only letters, numbers, or spaces';
      }
      if (issuerTicker.length > 10) {
        newErrors.issuerTicker = 'Issuer Ticker cannot exceed 10 characters';
      }
    }
    if (optionalFields['SEC Type'] && String(optionalFields['SEC Type']).length > 255) {
      newErrors.secType = 'SEC Type cannot exceed 255 characters';
    }
    if (optionalFields['Accrued Interest']) {
      const accruedInterest = parseNumericOptional(optionalFields['Accrued Interest']);
      if (accruedInterest === null) {
        newErrors.accruedInterest = 'Accrued Interest must be a valid number';
      } else if (accruedInterest > MAX_ACCRUED_INTEREST_VALUE) {
        newErrors.accruedInterest = 'Accrued Interest cannot exceed 99,999,999.99';
      }
    }
    if (optionalFields['Effective Duration']) {
      const effectiveDuration = parseNumericOptional(optionalFields['Effective Duration']);
      if (effectiveDuration === null) {
        newErrors.effectiveDuration = 'Effective Duration must be a valid number';
      } else if (effectiveDuration > MAX_EFFECTIVE_DURATION_VALUE) {
        newErrors.effectiveDuration = 'Effective Duration cannot exceed 999.9999';
      }
    }
    if (optionalFields.WAM) {
      const wam = parseNumericOptional(optionalFields.WAM);
      if (wam === null) {
        newErrors.wam = 'WAM must be a valid number';
      } else if (wam > MAX_WAM_VALUE) {
        newErrors.wam = 'WAM cannot be greater than 99.99';
      }
    }
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    // Format YYYYMM — e.g. August 2026 → 202608
    const yearMonth = parseInt(
      `${selectedYear}${String(selectedMonth).padStart(2, '0')}`,
      10
    );

    const payload = {
      SecurityDescription: securityDescription.trim(),
      Balance: parseBalance(balanceDisplay),
      YearMonth: yearMonth,
      IncludeInQuarterlyPerformanceReport: false,
      IncludeInTradingNotification: false,
      IncludeInMonthlyStatement: includeMonthly,
      ...(includeMonthly && linkedPfmamAccount ? { LinkedPfmamAccount: linkedPfmamAccount } : {}),
      ...optionalFields,
    };

    setCheckingDuplicate(true);
    const isDuplicate = await onBeforeReview(payload).catch(() => false);
    setCheckingDuplicate(false);
    if (isDuplicate) return;

    setPendingPayload(payload);
    setShowReview(true);
  };

  const handleConfirmReview = () => {
    if (pendingPayload) onSubmit(pendingPayload);
  };

  const handleBackFromReview = () => {
    setShowReview(false);
  };

  // ── Review screen ──
  if (showReview && pendingPayload) {
    const monthLabel = MONTHS.find((m) => m.value === selectedMonth)?.label || selectedMonth;
    const reviewRows = [
      { label: 'Holding Description', value: pendingPayload.SecurityDescription },
      {
        label: 'Month End Closing Market Value',
        value: `$${Number(pendingPayload.Balance).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      { label: 'Month End', value: `${monthLabel}/${selectedYear}` },
      {
        label: 'Monthly Statement',
        value: pendingPayload.IncludeInMonthlyStatement ? 'Yes' : 'No',
      },
      ...(pendingPayload.IncludeInMonthlyStatement && pendingPayload.LinkedPfmamAccount
        ? [{ label: 'Linked PFMAM Account', value: portfolioOptions.find((p) => (p.AccountUniqueID ?? p.PortfolioDescription) === pendingPayload.LinkedPfmamAccount)?.PortfolioDescription || pendingPayload.LinkedPfmamAccount }]
        : []),
      ...OPTIONAL_FIELDS.filter((f) => pendingPayload[f]).map((f) => ({
        label: f,
        value: pendingPayload[f],
      })),
    ];

    return (
      <>
        <Text
          label="Please review the new holding details before submitting."
          color={`${theme.colors.black_tertiary}`}
          fontSize={`${theme.font_size.size_xl}`}
          lineheight="24px"
          padding="0 0 16px 0"
        />
        <ReviewCard>
          {reviewRows.map(({ label, value }, i) => (
            <ReviewRow key={label} shaded={i % 2 === 0}>
              <ReviewLabel>{label}</ReviewLabel>
              <ReviewValue>{value}</ReviewValue>
            </ReviewRow>
          ))}
        </ReviewCard>
        <Footer>
          <ButtonAnt
            type="secondary"
            label="Go Back"
            onClick={handleBackFromReview}
            disabled={submitting}
            style={{ minWidth: '100px' }}
          />
          <Box display="flex" alignitems="center" gap="12px">
            {submitError && (
              <Text
                label={submitError}
                color={`${theme.colors.danger_red}`}
                fontSize={`${theme.font_size.size_l}`}
                lineheight="20px"
              />
            )}
            <ConfirmButton
              type="primary"
              linktextcolor={primaryColor}
              label={submitting ? 'Submitting...' : 'Confirm & Add'}
              onClick={handleConfirmReview}
              disabled={submitting}
              style={{ minWidth: '130px' }}
            />
          </Box>
        </Footer>
      </>
    );
  }

  return (
    <>
      <OverrideStyle />
      <FormGrid>

        {/* Holding Description */}
        <FieldGroup>
          <FieldLabel htmlFor="securityDescription">
            Holding Description <RequiredAsterisk>*</RequiredAsterisk>
          </FieldLabel>
          <AutoComplete
            value={securityDescription}
            onChange={(value) => {
              setSecurityDescription(sanitizeHoldingDescription(value));
              // User is typing free-form — clear any previously auto-filled values
              if (isApiSelection) {
                setIsApiSelection(false);
                setIncludeMonthly(false);
              }
              if (errors.securityDescription)
                setErrors((prev) => ({ ...prev, securityDescription: undefined }));
            }}
            onSelect={(value) => {
              const sanitizedValue = sanitizeHoldingDescription(value);
              setSecurityDescription(sanitizedValue);
              const match = securityDescriptionOptions.find(
                (opt) => opt && String(opt.SecurityDescription) === value
              );
              if (match) {
                setIsApiSelection(true);
              }
              if (errors.securityDescription)
                setErrors((prev) => ({ ...prev, securityDescription: undefined }));
            }}
            options={securityDescriptionOptions
              .filter((opt) => opt && opt.SecurityDescription)
              .filter((opt) =>
                !securityDescription ||
                String(opt.SecurityDescription).toLowerCase().includes(securityDescription.toLowerCase())
              )
              .map((opt) => ({ value: String(opt.SecurityDescription) }))}
            filterOption={false}
            getPopupContainer={() => document.body}
            dropdownStyle={{ zIndex: 1060 }}
            style={{ width: '100%' }}
            placeholder="Enter or select holding description"
            maxLength={255}
            status={errors.securityDescription ? 'error' : ''}
          />
          {errors.securityDescription && <ErrorText>{errors.securityDescription}</ErrorText>}
        </FieldGroup>

        {/* Month End Closing Market Value */}
        <FieldGroup>
          <FieldLabel htmlFor="monthEndBalance">
            Month End Closing Market Value <RequiredAsterisk>*</RequiredAsterisk>
          </FieldLabel>
          <Input
            id="monthEndBalance"
            value={balanceDisplay}
            onChange={handleBalanceChange}
            onBlur={handleBalanceBlur}
            placeholder="0.00"
            prefix="$"
            status={errors.balance ? 'error' : ''}
          />
          {errors.balance && <ErrorText>{errors.balance}</ErrorText>}
        </FieldGroup>

        {/* Month End */}
        <FieldGroup>
          <FieldLabel>
            Month End <RequiredAsterisk>*</RequiredAsterisk>
          </FieldLabel>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Select
              placeholder="Month"
              value={selectedMonth}
              onChange={(val) => {
                setSelectedMonth(val);
                if (errors.monthEnd) setErrors((prev) => ({ ...prev, monthEnd: undefined }));
              }}
              style={{ flex: 1 }}
              getPopupContainer={() => document.body}
              dropdownStyle={{ zIndex: 1060 }}
              status={errors.monthEnd ? 'error' : ''}
            >
              {MONTHS.map(({ value, label }) => (
                <Option key={value} value={value}>{label}</Option>
              ))}
            </Select>
            <Select
              placeholder="Year"
              value={selectedYear}
              onChange={(val) => {
                setSelectedYear(val);
                if (errors.monthEnd) setErrors((prev) => ({ ...prev, monthEnd: undefined }));
              }}
              style={{ flex: 1 }}
              getPopupContainer={() => document.body}
              dropdownStyle={{ zIndex: 1060 }}
              status={errors.monthEnd ? 'error' : ''}
            >
              {YEARS.map((year) => (
                <Option key={year} value={year}>{year}</Option>
              ))}
            </Select>
          </div>
          {errors.monthEnd && <ErrorText>{errors.monthEnd}</ErrorText>}
        </FieldGroup>

        {/* Monthly Statement */}
        <FieldGroup>
          <CheckboxRow>
            <CheckboxLabelRow>
              <Checkbox
                checked={includeMonthly}
                onChange={(e) => {
                  setIncludeMonthly(e.target.checked);
                  if (!e.target.checked) setLinkedPfmamAccount(null);
                }}
              >
                Monthly Statement
              </Checkbox>
            </CheckboxLabelRow>
            {includeMonthly && (
              <div style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <FieldLabel>
                  Portfolio <RequiredAsterisk>*</RequiredAsterisk>
                </FieldLabel>
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Search or select linked PFMAM account"
                  value={linkedPfmamAccount}
                  onChange={(val) => {
                    setLinkedPfmamAccount(val);
                    if (errors.linkedPfmamAccount)
                      setErrors((prev) => ({ ...prev, linkedPfmamAccount: undefined }));
                  }}
                  style={{ width: '100%' }}
                  getPopupContainer={() => document.body}
                  dropdownStyle={{ zIndex: 1060 }}
                  status={errors.linkedPfmamAccount ? 'error' : ''}
                >
                  {portfolioOptions.map((portfolio) => (
                    <Option
                      key={portfolio.AccountUniqueID ?? portfolio.PortfolioDescription}
                      value={portfolio.AccountUniqueID ?? portfolio.PortfolioDescription}
                    >
                      {portfolio.PortfolioDescription}
                    </Option>
                  ))}
                </Select>
                {errors.linkedPfmamAccount && <ErrorText>{errors.linkedPfmamAccount}</ErrorText>}
              </div>
            )}
          </CheckboxRow>
        </FieldGroup>

        {/* Optional Additional Details */}
        <SectionBlock>
          <CollapseToggle onClick={() => setShowOptional((prev) => !prev)}>
            <CollapseIcon>
              {showOptional ? <DownOutlined /> : <RightOutlined />}
            </CollapseIcon>
            Optional Additional Details
          </CollapseToggle>
          {showOptional && (
            <OptionalContent>
              <TwoColumnGrid>
                {OPTIONAL_FIELDS.map((field) => (
                  <FieldGroup key={field}>
                    <FieldLabel htmlFor={`opt-${field}`} style={{ color: theme.colors.grey_tertiary }}>
                      {field}
                    </FieldLabel>
                    {field === 'Maturity Date' ? (
                      <>
                        <DatePicker
                          id={`opt-${field}`}
                          value={optionalFields[field] ? moment(optionalFields[field], 'MM/DD/YYYY', true) : null}
                          onChange={(date) => {
                            handleOptionalFieldChange(field, date ? date.format('MM/DD/YYYY') : '');
                            if (errors.maturityDate) {
                              setErrors((prev) => ({ ...prev, maturityDate: undefined }));
                            }
                          }}
                          disabledDate={(current) => current && current < moment().startOf('day')}
                          format="MM/DD/YYYY"
                          placeholder="MM/DD/YYYY"
                          className="suffix-left"
                          dropdownClassName="single-date-picker"
                          style={{ width: '100%' }}
                          getPopupContainer={(trigger) => trigger.parentElement}
                        />
                        {errors.maturityDate && <ErrorText>{errors.maturityDate}</ErrorText>}
                      </>
                    ) : RATING_FIELD_TO_KEY[field] ? (
                      <Select
                        id={`opt-${field}`}
                        showSearch
                        allowClear
                        value={optionalFields[field] || undefined}
                        onChange={(val) => {
                          handleOptionalFieldChange(field, val || '');
                        }}
                        placeholder="Select"
                        style={{ width: '100%' }}
                        getPopupContainer={() => document.body}
                        dropdownStyle={{ zIndex: 1060 }}
                        filterOption={(input, option) =>
                          String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {(ratingOptions[RATING_FIELD_TO_KEY[field]] || []).map((rating) => (
                          <Option key={rating} value={rating}>{rating}</Option>
                        ))}
                      </Select>
                    ) : field === 'SEC Type' ? (
                      <Select
                        id={`opt-${field}`}
                        showSearch
                        allowClear
                        value={optionalFields[field] || undefined}
                        onChange={(val) => {
                          handleOptionalFieldChange(field, val || '');
                          if (errors.secType) {
                            setErrors((prev) => ({ ...prev, secType: undefined }));
                          }
                        }}
                        placeholder="Select"
                        style={{ width: '100%' }}
                        getPopupContainer={() => document.body}
                        dropdownStyle={{ zIndex: 1060 }}
                        filterOption={(input, option) =>
                          String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {securityTypeOptions.map((securityType) => (
                          <Option key={securityType} value={securityType}>{securityType}</Option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        id={`opt-${field}`}
                        value={optionalFields[field] || ''}
                        status={
                          field === 'YTM at Cost'
                            ? errors.ytmAtCost
                              ? 'error'
                              : ''
                            : field === 'YTM Market'
                            ? errors.ytmMarket
                              ? 'error'
                              : ''
                            : field === 'Issuer Ticker'
                            ? errors.issuerTicker
                              ? 'error'
                              : ''
                            : field === 'Accrued Interest'
                            ? errors.accruedInterest
                              ? 'error'
                              : ''
                            : field === 'WAM'
                            ? errors.wam
                              ? 'error'
                              : ''
                            : field === 'Effective Duration'
                            ? errors.effectiveDuration
                              ? 'error'
                              : ''
                            : ''
                        }
                        onChange={(e) => {
                          let val = e.target.value;
                          if (PERCENT_FIELDS.has(field)) {
                            val = sanitizePercentValue(val);
                            if (field === 'YTM at Cost' && errors.ytmAtCost) {
                              setErrors((prev) => ({ ...prev, ytmAtCost: undefined }));
                            }
                            if (field === 'YTM Market' && errors.ytmMarket) {
                              setErrors((prev) => ({ ...prev, ytmMarket: undefined }));
                            }
                          } else if (field === 'Issuer Ticker') {
                            val = val.replace(/[^a-z0-9 ]/gi, '');
                            if (errors.issuerTicker) {
                              setErrors((prev) => ({ ...prev, issuerTicker: undefined }));
                            }
                          } else if (field === 'SEC Type') {
                            val = val.slice(0, 255);
                            if (errors.secType) {
                              setErrors((prev) => ({ ...prev, secType: undefined }));
                            }
                          } else if (CURRENCY_FIELDS.has(field)) {
                            val = sanitizeNumericWithScale(val, 8, 2);
                            if (errors.accruedInterest) {
                              setErrors((prev) => ({ ...prev, accruedInterest: undefined }));
                            }
                          } else if (NUMBER_FIELDS.has(field)) {
                            val = sanitizeNumericWithScale(val, 3, 4);
                            if (errors.effectiveDuration) {
                              setErrors((prev) => ({ ...prev, effectiveDuration: undefined }));
                            }
                          } else if (field === 'WAM') {
                            val = sanitizeNumericWithScale(val, 2, 2);
                            if (errors.wam) {
                              setErrors((prev) => ({ ...prev, wam: undefined }));
                            }
                          }
                          handleOptionalFieldChange(field, val);
                        }}
                        onBlur={(e) => {
                          if (PERCENT_FIELDS.has(field) && e.target.value) {
                            const numeric = parseFloat(e.target.value);
                            if (!isNaN(numeric)) {
                              const clamped = Math.min(numeric, 10);
                              handleOptionalFieldChange(field, clamped.toFixed(2));
                            }
                          } else if (field === 'Accrued Interest' && e.target.value) {
                            handleOptionalFieldChange(field, formatNumericWithCommas(e.target.value, 2));
                          } else if (field === 'Effective Duration' && e.target.value) {
                            handleOptionalFieldChange(field, formatNumericWithCommas(e.target.value, 4));
                          } else if (field === 'WAM' && e.target.value) {
                            const wam = parseFloat(e.target.value);
                            if (!isNaN(wam)) {
                              handleOptionalFieldChange(field, Math.min(wam, MAX_WAM_VALUE).toFixed(2));
                            }
                          }
                        }}
                        placeholder="—"
                        maxLength={
                          field === 'Issuer Ticker'
                            ? 10
                            : field === 'SEC Type'
                            ? 255
                            : field === 'WAM'
                            ? 5
                            : PERCENT_FIELDS.has(field)
                            ? 5
                            : undefined
                        }
                        suffix={PERCENT_FIELDS.has(field) ? '%' : undefined}
                        {...(CURRENCY_FIELDS.has(field) ? { prefix: '$' } : {})}
                      />
                    )}
                    {field === 'YTM at Cost' && errors.ytmAtCost && <ErrorText>{errors.ytmAtCost}</ErrorText>}
                    {field === 'YTM Market' && errors.ytmMarket && <ErrorText>{errors.ytmMarket}</ErrorText>}
                    {field === 'Issuer Ticker' && errors.issuerTicker && <ErrorText>{errors.issuerTicker}</ErrorText>}
                    {field === 'SEC Type' && errors.secType && <ErrorText>{errors.secType}</ErrorText>}
                    {field === 'Accrued Interest' && errors.accruedInterest && <ErrorText>{errors.accruedInterest}</ErrorText>}
                    {field === 'WAM' && errors.wam && <ErrorText>{errors.wam}</ErrorText>}
                    {field === 'Effective Duration' && errors.effectiveDuration && <ErrorText>{errors.effectiveDuration}</ErrorText>}
                  </FieldGroup>
                ))}
              </TwoColumnGrid>
            </OptionalContent>
          )}
        </SectionBlock>

      </FormGrid>

      {/* Footer */}
      <Footer>
        <ButtonAnt
          type="secondary"
          label="Cancel"
          onClick={onCancel}
          disabled={submitting}
          style={{ minWidth: '100px' }}
        />
        <Box display="flex" alignitems="center" gap="12px">
          {submitError && (
            <Text
              label={submitError}
              color={`${theme.colors.danger_red}`}
              fontSize={`${theme.font_size.size_l}`}
              lineheight="20px"
            />
          )}
          <ConfirmButton
            type="primary"
            linktextcolor={primaryColor}
            label={checkingDuplicate ? 'Checking...' : 'Review & Add'}
            onClick={handleSubmit}
            disabled={submitting || checkingDuplicate}
            style={{ minWidth: '120px' }}
          />
        </Box>
      </Footer>
    </>
  );
};

export default ThirdPartyHoldingsAddModal;
